/**
 * AssistantHost — mounts the unified assistant globally in AppShell: owns the
 * chat session, the Cmd/Ctrl+. toggle,
 * and the `?assistant=<conversationId>` deep link, plus the floating
 * launcher puck and the floating chat card it opens. Open/close state and
 * panel exclusivity stay with AppShell.
 *
 * Also owns the captive-takeover choreography an onboarding runs through
 * (`onboardingFlow.ts`'s `TakeoverPhase`) — one physical object throughout:
 * the puck winds up and flies to the content area's center, its own mark
 * morphs into the takeover surface as it expands, the conversation runs, a
 * held beat on finish, then the reverse. The phase transitions themselves
 * are pure (`nextTakeoverPhase`); this component is the async orchestrator
 * that calls `AssistantLauncherHandle`'s flight/pulse methods, waits on the
 * takeover surface's own `onAnimationComplete`, and drives `setPhase`
 * between them.
 */

import React from 'react';
import { useReducedMotion } from 'framer-motion';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { assistantOnboarding, ONBOARDING_KICKOFF_MARKER_KIND, type AssistantOnboardingId } from '@chatsdet/types';
import { useAssistantChat } from './useAssistantChat';
import { useIsFirstRun } from './useIsFirstRun';
import { useAuth } from '../../contexts/AuthContext';
import { AssistantPanel, type TakeoverBox, type TakeoverGeometry, type TakeoverMark } from './AssistantPanel';
import { AssistantLauncher, type AssistantLauncherHandle } from './AssistantLauncher';
import { buildLocation } from './pageRegistry';
import { PUCK_SIZE, TAKEOVER_PULSE_MAGNITUDE, centerCircleRect, type Corner, type Rect } from './cornerPin';
import { useContentAreaRect } from './contentAreaRect';
import {
  kickoffMetadata,
  markKickoffSent,
  nextTakeoverPhase,
  shouldSendKickoff,
  startAsk,
  startKickoff,
  startOnboarding,
  type AskState,
  type KickoffRequest,
  type KickoffState,
  type OnboardingState,
  type TakeoverPhase,
} from './onboardingFlow';
import { NotificationCtx } from '../notifications/NotificationContext';

export type AssistantPromptSeed = { text: string; nonce: number };

/** A page's request to run an onboarding — `nonce` re-fires even for the same id. */
export type AssistantOnboardingRequest = {
  id: AssistantOnboardingId;
  params: Record<string, string> | undefined;
  nonce: number;
};

export type AssistantHostProps = {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  /** Open the panel, closing sibling panels (AppShell owns exclusivity). */
  onRequestOpen: () => void;
  /** Pre-fill the composer (e.g. docs "Ask about this"). Keyed by nonce. */
  promptSeed?: AssistantPromptSeed | null;
  /** A page handing the assistant a guided onboarding to run. */
  onboardingRequest?: AssistantOnboardingRequest | null;
  /** A page's request to open the ordinary panel with a hidden kickoff turn. Keyed by nonce. */
  kickoffRequest?: (KickoffRequest & { nonce: number }) | null;
  /** A page's request to open the panel on a FRESH thread and send `text` as a visible turn. Keyed by nonce. */
  askRequest?: { text: string; params: Record<string, string>; nonce: number } | null;
  /** Corner the launcher puck is pinned to (persisted in user settings). */
  corner: Corner;
  onCornerChange: (corner: Corner) => void;
};

const ZERO_BOX: TakeoverBox = { rect: { top: 0, left: 0, width: PUCK_SIZE, height: PUCK_SIZE }, borderRadius: 9999 };
const MARK_SIZE = 44;
/** Beat after the puck lands before the surface starts growing — "the eye lands on the mark first". */
const ARRIVAL_HOLD_MS = 120;
/** Hold on the assistant's own "done" line before the surface starts to shrink. */
const FINISH_HOLD_MS = 600;
/** Upper bound on waiting for the streaming turn to settle before holding anyway. */
const FINISH_WAIT_CAP_MS = 1500;
const EXPAND_DURATION_S = 0.45;
const COLLAPSE_DURATION_S = 0.42;
const REDUCED_DURATION_S = 0.2;
/** Fast start, soft landing — the surface's growth. */
const EXPAND_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];
/** The mirror — a sharper, gathering contraction. */
const COLLAPSE_EASE: [number, number, number, number] = [0.7, 0, 0.84, 0];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mainRect(): Rect | null {
  const el = document.querySelector('[data-app-main]');
  const rect = el?.getBoundingClientRect();
  return rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null;
}

export function AssistantHost({
  isOpen,
  onClose,
  onToggle,
  onRequestOpen,
  promptSeed,
  onboardingRequest,
  kickoffRequest,
  askRequest,
  corner,
  onCornerChange,
}: AssistantHostProps) {
  const router = useRouter();
  const routerState = useRouterState({ select: (state) => state.location });
  const queryClient = useQueryClient();
  const reducedMotion = useReducedMotion() ?? false;
  const { onAssistantWake } = React.useContext(NotificationCtx);
  const launcherRef = React.useRef<AssistantLauncherHandle>(null);

  // ---------------------------------------------------------------------------
  // Onboarding + takeover phase state. `onboarding` is the pure record from
  // `onboardingFlow.ts`; `phase` is the pure takeover-choreography phase from
  // the same module. Both are driven only through their exported helpers —
  // no parallel state shape duplicated here.
  // ---------------------------------------------------------------------------
  const [onboarding, setOnboarding] = React.useState<OnboardingState | null>(null);
  const [phase, setPhase] = React.useState<TakeoverPhase>('idle');
  const phaseRef = React.useRef(phase);
  phaseRef.current = phase;

  // A plain "thread with kickoff" — the ordinary corner panel opened with a
  // hidden first turn (see `onboardingFlow.ts`'s `KickoffState`). Unlike
  // `onboarding`, this never drives the captive-takeover phase machine.
  const [kickoff, setKickoff] = React.useState<KickoffState | null>(null);

  // A plain "ask" — the ordinary corner panel opened on a FRESH thread with
  // `text` sent as a normal VISIBLE first turn (contrast `kickoff`, whose
  // turn is hidden behind a marker). See `onboardingFlow.ts`'s `AskState`.
  const [ask, setAsk] = React.useState<AskState | null>(null);

  // The takeover surface's mount-only starting box (frozen the instant flight
  // ends) and its frozen collapse target (frozen the instant exit begins) —
  // both refs/state rather than derived, because both must NOT track a live
  // content-area resize the way the "expanding"/"open" target does.
  const mountBoxRef = React.useRef<TakeoverBox>(ZERO_BOX);
  const [collapseBox, setCollapseBox] = React.useState<TakeoverBox | null>(null);
  // A pending entrance flight's staleness guard: bumped by `cancelTakeover`
  // so a mid-flight cancel doesn't let the original `beginTakeover` continue
  // on into `expanding` once its flight promise eventually resolves.
  const flightTokenRef = React.useRef(0);
  // Set by `onOnboardingFinished` before requesting the `finish` transition;
  // read by the `returning` effect to pick the reappearance pulse (bigger,
  // celebratory on a real finish; the ordinary dismiss pop on any other
  // exit), and reset once consumed.
  const exitReasonRef = React.useRef<'finish' | 'cancel'>('cancel');
  const pendingNavigateRef = React.useRef<{ pageId: string; params: Record<string, string> } | null>(null);
  const pendingNewChatRef = React.useRef(false);

  const showTakeover =
    phase === 'expanding' || phase === 'open' || phase === 'finishing' || phase === 'collapsing';
  // `pushed` un-pushes a beat before `locked` releases — see `contentAreaRect.ts`.
  const contentRect = useContentAreaRect(phase !== 'idle', phase !== 'idle' && phase !== 'returning');

  const isGrowingRect = phase === 'expanding' || phase === 'open' || phase === 'finishing';
  const rect = isGrowingRect ? (contentRect ?? mountBoxRef.current.rect) : (collapseBox?.rect ?? mountBoxRef.current.rect);
  const borderRadius: number | number[] = reducedMotion
    ? isGrowingRect
      ? 0
      : 9999
    : phase === 'expanding'
      ? [9999, 28, 0]
      : isGrowingRect
        ? 0
        : 9999;

  const propTransition = (duration: number, ease: [number, number, number, number]) => ({
    duration: reducedMotion ? REDUCED_DURATION_S : duration,
    ease,
  });
  const rectTransition: TakeoverGeometry['transition'] = isGrowingRect
    ? {
        top: propTransition(EXPAND_DURATION_S, EXPAND_EASE),
        left: propTransition(EXPAND_DURATION_S, EXPAND_EASE),
        width: propTransition(EXPAND_DURATION_S, EXPAND_EASE),
        height: propTransition(EXPAND_DURATION_S, EXPAND_EASE),
        borderRadius:
          !reducedMotion && phase === 'expanding'
            ? { ...propTransition(EXPAND_DURATION_S, EXPAND_EASE), times: [0, 0.55, 1] }
            : propTransition(EXPAND_DURATION_S, EXPAND_EASE),
      }
    : {
        top: propTransition(COLLAPSE_DURATION_S, COLLAPSE_EASE),
        left: propTransition(COLLAPSE_DURATION_S, COLLAPSE_EASE),
        width: propTransition(COLLAPSE_DURATION_S, COLLAPSE_EASE),
        height: propTransition(COLLAPSE_DURATION_S, COLLAPSE_EASE),
        borderRadius: propTransition(COLLAPSE_DURATION_S, COLLAPSE_EASE),
      };

  // The center-mark morph — null (and so unmounted) outside expanding/
  // collapsing, and always null under reduced motion (a plain cross-fade
  // instead, per the puck's own transition and the surface's transition
  // above). Re-mounting fresh for each of the two phases is deliberate: each
  // needs its OWN `initial`, and framer only honors `initial` at mount.
  const mark: TakeoverMark | null = reducedMotion
    ? null
    : phase === 'expanding'
      ? {
          top: mountBoxRef.current.rect.top + (PUCK_SIZE - MARK_SIZE) / 2,
          left: mountBoxRef.current.rect.left + (PUCK_SIZE - MARK_SIZE) / 2,
          initialOpacity: 1,
          initialScale: 1,
          opacity: 0,
          scale: 1.35,
          transition: { duration: 0.26, ease: 'easeOut' },
        }
      : phase === 'collapsing'
        ? {
            top: (collapseBox?.rect ?? mountBoxRef.current.rect).top + (PUCK_SIZE - MARK_SIZE) / 2,
            left: (collapseBox?.rect ?? mountBoxRef.current.rect).left + (PUCK_SIZE - MARK_SIZE) / 2,
            initialOpacity: 0,
            initialScale: 1.35,
            opacity: 1,
            scale: 1,
            transition: { duration: 0.2, delay: COLLAPSE_DURATION_S - 0.2 },
          }
        : null;

  /** Fires when the surface's rect/radius tween finishes — advances expanding→open or collapsing→returning. */
  const handleRectAnimationComplete = React.useCallback(() => {
    setPhase((current) => {
      if (current === 'expanding') return nextTakeoverPhase(current, { type: 'expanded' });
      if (current === 'collapsing') return nextTakeoverPhase(current, { type: 'collapsed' });
      return current;
    });
  }, []);

  // `enabled: isOpen || showTakeover` — the takeover mints/loads its
  // conversation even though the ordinary corner panel is never opened for
  // it.
  //
  // Both `kickoff` and `ask` can be non-null at once (neither mint clears the
  // other, and an unsent kickoff is never retired), so the match against the
  // live `conversationId` has to happen where that id actually lives — inside
  // `useAssistantChat`, which already matches on `conversationId` for its own
  // onboarding overlay. Pre-selecting a single pin here, ahead of the call,
  // required reading `chat.conversationId` through a ref that only catches up
  // a render AFTER the id it's tracking changes — so on the very render a
  // freshly minted conversation id first appears, the pre-selected pin would
  // resolve to null and the pin would silently drop for that turn. Handing
  // over every candidate and letting the hook match on its own live
  // `conversationId` removes that lag entirely.
  const chat = useAssistantChat({
    enabled: isOpen || showTakeover,
    panelOpen: isOpen || showTakeover,
    onboarding,
    threadPins: [kickoff, ask]
      .filter((state) => state !== null)
      .map((state) => ({ conversationId: state.conversationId, params: state.params })),
    onOnboardingFinished: (result) => {
      const navigateTo = (result as { navigateTo?: { pageId: string; params: Record<string, string> } } | null)
        ?.navigateTo;
      pendingNavigateRef.current = navigateTo ?? null;
      exitReasonRef.current = 'finish';
      setPhase((current) => nextTakeoverPhase(current, { type: 'finish' }));
    },
  });

  // ---------------------------------------------------------------------------
  // Enter: wind up, fly to center, hold, expand.
  // ---------------------------------------------------------------------------
  const beginTakeover = React.useCallback(
    async (id: AssistantOnboardingId, params: Record<string, string> | undefined) => {
      // Only ever starts from idle — a second request while one is already
      // running (or mid-exit) is a no-op rather than stomping the phase
      // machine underneath it.
      if (phaseRef.current !== 'idle') return;

      // Step 1 — close the ordinary corner panel if it's open; it animates
      // out at the corner exactly as it always has.
      onClose();

      exitReasonRef.current = 'cancel';
      // A leaked ref from a PRIOR exit must never fire against this new run
      // (e.g. a stale queued navigate popping the corner panel once THIS
      // onboarding finishes).
      pendingNavigateRef.current = null;
      pendingNewChatRef.current = false;

      const cid = chat.startNewChat();
      setOnboarding(startOnboarding(id, params, cid));
      setPhase((current) => nextTakeoverPhase(current, { type: 'start' }));

      const rectNow = mainRect() ?? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      const circle = centerCircleRect(rectNow, PUCK_SIZE);
      mountBoxRef.current = { rect: circle, borderRadius: 9999 };

      const token = ++flightTokenRef.current;
      // Wind-up (squeeze inward, launch on the rebound) → flight spring → a
      // landing thunk + delayed pop, all inside `flyTo` — see `useCornerPin.ts`.
      await (launcherRef.current?.flyTo({ x: circle.left, y: circle.top }, { windUp: true }) ??
        Promise.resolve());
      if (flightTokenRef.current !== token) return; // cancelled mid-flight

      // Hold a beat so the eye lands on the mark before it starts to grow.
      await sleep(reducedMotion ? 0 : ARRIVAL_HOLD_MS);
      if (flightTokenRef.current !== token) return;

      setPhase((current) => nextTakeoverPhase(current, { type: 'arrived' }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chat.startNewChat/onClose are stable per-render helpers
    [reducedMotion]
  );

  /** User-initiated exit — Escape, puck click mid-flight, close, new chat. Always wins. */
  const cancelTakeover = React.useCallback(() => {
    const current = phaseRef.current;
    if (current === 'idle' || current === 'collapsing' || current === 'returning') return;
    exitReasonRef.current = 'cancel';
    if (current === 'flying') {
      // No surface exists yet — invalidate the pending entrance flight and
      // just reverse the puck home; framer retargets the SAME motion values
      // (no snapping).
      flightTokenRef.current += 1;
      setPhase((phaseNow) => nextTakeoverPhase(phaseNow, { type: 'cancel' }));
      setOnboarding(null);
      void (launcherRef.current?.returnToCorner() ?? Promise.resolve());
      return;
    }
    setPhase(nextTakeoverPhase(current, { type: 'cancel' }));
  }, []);

  const handleTakeoverNewChat = React.useCallback(() => {
    pendingNewChatRef.current = true;
    cancelTakeover();
  }, [cancelTakeover]);

  // Start a new onboarding whenever a page hands one off.
  React.useEffect(() => {
    if (!onboardingRequest) return;
    void beginTakeover(onboardingRequest.id, onboardingRequest.params);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the request's nonce
  }, [onboardingRequest?.nonce]);

  // Start a new thread kickoff whenever a page hands one off. Unlike the
  // takeover onboarding, this never touches `phase` — it just mints a fresh
  // conversation for the ordinary corner panel (already opened by the
  // caller via `onRequestOpen`/`updateSettings`). Dropped entirely if the
  // takeover surface is mid-flight/open — the page-filling surface must
  // never be preempted by an unrelated thread.
  React.useEffect(() => {
    if (!kickoffRequest) return;
    if (phaseRef.current !== 'idle') return;
    const cid = chat.startNewChat();
    setKickoff(startKickoff(kickoffRequest, cid));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the request's nonce
  }, [kickoffRequest?.nonce]);

  // Send the hidden kickoff turn once the ordinary panel is open and the
  // chat has caught up to the conversation the kickoff was started against.
  React.useEffect(() => {
    if (!isOpen) return;
    if (!shouldSendKickoff(kickoff, chat.conversationId, chat.isPreparing)) return;
    const request = kickoff!;
    setKickoff((current) => (current ? markKickoffSent(current) : current));
    void chat.sendUserTurn(request.text, kickoffMetadata(request));
  }, [isOpen, kickoff, chat.conversationId, chat.isPreparing, chat.sendUserTurn]);

  // Once the kickoff has been sent, the user switching to a different
  // conversation retires it — it's now just an ordinary thread, and its
  // params must stop leaking into whatever thread is open now.
  React.useEffect(() => {
    if (kickoff?.kickoffSent && chat.conversationId !== kickoff.conversationId) {
      setKickoff(null);
    }
  }, [kickoff, chat.conversationId]);

  // Send the hidden kickoff once the surface is fully open and the chat has
  // caught up to the conversation the onboarding was started against.
  React.useEffect(() => {
    if (phase !== 'open') return;
    if (!shouldSendKickoff(onboarding, chat.conversationId, chat.isPreparing)) return;
    const info = assistantOnboarding(onboarding!.id);
    setOnboarding((current) => (current ? markKickoffSent(current) : current));
    void chat.sendUserTurn(info.kickoffText, {
      kind: ONBOARDING_KICKOFF_MARKER_KIND,
      onboardingId: onboarding!.id,
    });
  }, [phase, onboarding, chat.conversationId, chat.isPreparing, chat.sendUserTurn]);

  // `finishing`: hold until the streaming turn settles (or the cap elapses),
  // plus the hold beat itself, then request the collapse.
  const finishingStartedAtRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (phase !== 'finishing') {
      finishingStartedAtRef.current = null;
      return;
    }
    if (finishingStartedAtRef.current === null) finishingStartedAtRef.current = Date.now();

    let holdTimer: ReturnType<typeof setTimeout> | undefined;
    let capTimer: ReturnType<typeof setTimeout> | undefined;
    const proceed = () => {
      holdTimer = setTimeout(() => {
        setPhase((current) => nextTakeoverPhase(current, { type: 'holdComplete' }));
      }, reducedMotion ? 0 : FINISH_HOLD_MS);
    };

    if (!chat.isSending) {
      proceed();
    } else {
      const elapsed = Date.now() - finishingStartedAtRef.current;
      capTimer = setTimeout(proceed, Math.max(0, FINISH_WAIT_CAP_MS - elapsed));
    }
    return () => {
      if (holdTimer) clearTimeout(holdTimer);
      if (capTimer) clearTimeout(capTimer);
    };
  }, [phase, chat.isSending, reducedMotion]);

  // `collapsing`: freeze the collapse target and navigate — AFTER the
  // collapse begins, not after it ends, so the destination page can change
  // under the still-flying puck.
  React.useEffect(() => {
    if (phase !== 'collapsing') return;
    const rectNow = mainRect() ?? mountBoxRef.current.rect;
    setCollapseBox({ rect: centerCircleRect(rectNow, PUCK_SIZE), borderRadius: 9999 });

    const navigateTo = pendingNavigateRef.current;
    pendingNavigateRef.current = null;
    if (navigateTo) {
      router
        .navigate(buildLocation(navigateTo.pageId, navigateTo.params))
        .catch((error: unknown) => console.error('[Assistant] onboarding navigate failed', error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router is stable
  }, [phase]);

  // `returning`: the surface has fully collapsed — pulse (celebratory on a
  // real finish, the ordinary pop otherwise), fly the puck home, land, then
  // fully clear the onboarding. A queued "New chat" fires only now, once the
  // OLD conversation has finished visually leaving.
  React.useEffect(() => {
    if (phase !== 'returning') return;
    let cancelled = false;
    void (async () => {
      const magnitude =
        exitReasonRef.current === 'finish' ? TAKEOVER_PULSE_MAGNITUDE * 1.5 : TAKEOVER_PULSE_MAGNITUDE;
      launcherRef.current?.pulse(magnitude);
      await (launcherRef.current?.returnToCorner() ?? Promise.resolve());
      if (cancelled) return;
      setPhase((current) => nextTakeoverPhase(current, { type: 'returned' }));
      setOnboarding(null);
      exitReasonRef.current = 'cancel';
      if (pendingNewChatRef.current) {
        pendingNewChatRef.current = false;
        chat.startNewChat();
        onRequestOpen();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chat.startNewChat/onRequestOpen are stable per-render helpers
  }, [phase]);

  // Escape cancels the takeover from anywhere in it — mirrors clicking the
  // puck mid-flight (`onClickOverride` below) or the surface's own Close.
  // Skipped when something else already consumed the key (a dialog, a
  // popover's own Escape handler) or the focus is inside one — the composer's
  // "add from library" picker is a React Aria `Popover`, and its own Escape
  // must close just the popover, not the whole takeover underneath it.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || phaseRef.current === 'idle') return;
      if (event.defaultPrevented) return;
      // React Aria's `Popover` (the composer's library picker) renders its
      // content with `role="dialog"` — verified live: `[role="dialog"]` is
      // exactly what's on screen while it's open. `data-rac` alone is far
      // too broad (React Aria stamps it on nearly every interactive element
      // in the app, not just open overlays), so it's deliberately not used.
      const target = event.target as HTMLElement | null;
      if (target?.closest('[role="dialog"]')) return;
      event.preventDefault();
      cancelTakeover();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cancelTakeover]);

  // Wake-up: background work the assistant started (Flow Autobuild) finished
  // while the user was elsewhere. Always auto-open onto the conversation it
  // ran in — no badge, per product decision — and refetch so the two
  // messages `assistant-wake.ts` posted (and the now-completed activity)
  // appear immediately. Opening the panel here flips `isOpen`, which
  // `AssistantLauncher`'s own open/dismiss effect already turns into a
  // `pulse()` — no new physics wiring needed. Ignored entirely mid-takeover:
  // the page-filling surface must never be yanked to an unrelated thread.
  React.useEffect(() => {
    return onAssistantWake((event) => {
      if (phaseRef.current !== 'idle') return;
      chat.switchConversation(event.conversationId);
      onRequestOpen();
      // Switching to a DIFFERENT conversation re-seeds the thread on its own.
      // Waking into the one already on screen does not: `useMessageSync` seeds
      // a conversation once and then refuses to re-seed, so the server-authored
      // turn would sit in the query cache, unseen. Reload explicitly.
      void chat.reloadMessages();
      void queryClient.invalidateQueries({
        queryKey: ['assistant', 'activities', event.conversationId],
      });
    });
  }, [
    onAssistantWake,
    chat.switchConversation,
    chat.reloadMessages,
    onRequestOpen,
    queryClient,
  ]);

  // Keyboard shortcut: Cmd/Ctrl + . toggles the panel — or, mid-takeover,
  // cancels it. Never toggles `isOpen` while the surface owns the screen:
  // that would flip `assistantPanelOpen` true underneath it, popping the
  // ordinary corner card open the instant the takeover returns to idle.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '.' || !(event.metaKey || event.ctrlKey)) return;
      if (phaseRef.current !== 'idle') {
        event.preventDefault();
        cancelTakeover();
        return;
      }
      const target = event.target as HTMLElement | null;
      const inEditable =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      // Allow toggling OPEN from anywhere; don't steal the combo while typing
      // into some other input when the panel is already open.
      if (inEditable && isOpen) return;
      event.preventDefault();
      onToggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onToggle, cancelTakeover]);

  // Deep link: ?assistant=<conversationId> opens that shared thread. Read the
  // raw URL so it works on any route; strip the param so refresh doesn't
  // reopen. (Same pattern as the ?support= deep link.)
  const locationSearch = routerState.search;
  React.useEffect(() => {
    const conversationId = new URLSearchParams(window.location.search).get('assistant');
    if (!conversationId) return;
    chat.switchConversation(conversationId);
    onRequestOpen();
    router.navigate({
      to: routerState.pathname,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next.assistant;
        return next;
      },
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on navigation
  }, [locationSearch]);

  // First run: a new org (no applications or tests) gets the assistant opened
  // once, showing the welcome (see `FirstRunWelcome`). Remembered per browser.
  const isFirstRun = useIsFirstRun();
  const { user } = useAuth();
  React.useEffect(() => {
    if (!isFirstRun || isOpen || !user?.orgId) return;
    const openedKey = `assistantFirstRunOpened:${user.orgId}`;
    try {
      if (localStorage.getItem(openedKey)) return;
      onRequestOpen();
      localStorage.setItem(openedKey, '1');
    } catch {
      // Storage unavailable (private mode): skip rather than reopen on every render.
    }
  }, [isFirstRun, isOpen, user?.orgId, onRequestOpen]);

  // Composer pre-fill (docs "Ask about this…" reroutes here when the flag is on).
  const lastSeedNonceRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!promptSeed || promptSeed.nonce === lastSeedNonceRef.current) return;
    lastSeedNonceRef.current = promptSeed.nonce;
    chat.setChatInput(promptSeed.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptSeed]);

  // "Ask" (open-questions "Answer" buttons, etc.) — mint a fresh conversation
  // for `askRequest.text` the moment the request arrives. The actual send is
  // NOT issued here: `chat.startNewChat()` only updates the `conversationId`
  // state asynchronously (it mints a new id and re-renders), so a send fired
  // in this same tick would still be going through the OLD chat instance
  // (`useChatCore`'s `id`/transport are keyed on `conversationId`, and
  // `core.sendText`'s closure only sees the new one once React has
  // re-rendered with it). `startNewChat` returns the freshly minted id so it
  // can be threaded through `ask.conversationId`, and the effect below sends
  // only once the live `chat.conversationId` has actually caught up to it —
  // the same gate `kickoffRequest` already relies on, not a `setTimeout`.
  const lastAskNonceRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!askRequest || askRequest.nonce === lastAskNonceRef.current) return;
    // Same guard the kickoff mint has: a captive takeover owns the whole page
    // and its send/finish gates are keyed on `conversationId`, so minting a new
    // conversation under it would silently strand the running onboarding.
    if (phaseRef.current !== 'idle') return;
    lastAskNonceRef.current = askRequest.nonce;
    const cid = chat.startNewChat();
    setAsk(startAsk(askRequest.text, askRequest.params, cid));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the request's nonce; chat.startNewChat is a stable per-render helper
  }, [askRequest?.nonce]);

  // Send the ask once the ordinary panel is open and the chat has caught up
  // to the conversation it was started against. `metadata: {}` — unlike a
  // kickoff, this carries no `kind`, so the panel renders it as an ordinary
  // visible user bubble rather than a marker divider.
  //
  // A sent ask is deliberately NOT retired afterward (contrast `kickoff`,
  // above): the pin stays so that returning to this thread — e.g. from
  // history, while sitting on a different application's page — still grounds
  // on the property/params it was started for, since this assistant *writes*
  // answers into the product map. `conversationId` matching — every candidate
  // goes to `useAssistantChat` as `threadPins`, which selects against its own
  // live conversation — is what keeps it from ever applying elsewhere. The pin lives in
  // component state only, so a reload drops it — best-effort, not durable.
  React.useEffect(() => {
    if (!isOpen) return;
    if (!shouldSendKickoff(ask, chat.conversationId, chat.isPreparing)) return;
    const request = ask!;
    setAsk((current) => (current ? markKickoffSent(current) : current));
    void chat.sendUserTurn(request.text, {});
  }, [isOpen, ask, chat.conversationId, chat.isPreparing, chat.sendUserTurn]);

  return (
    <>
      <AssistantLauncher
        ref={launcherRef}
        corner={corner}
        onCornerChange={onCornerChange}
        isOpen={isOpen}
        onToggle={onToggle}
        hidden={phase === 'expanding' || phase === 'open' || phase === 'finishing' || phase === 'collapsing'}
        onClickOverride={phase === 'flying' ? cancelTakeover : undefined}
      />
      {showTakeover ? (
        <AssistantPanel
          isOpen
          chat={chat}
          onClose={cancelTakeover}
          corner={corner}
          onNewChat={handleTakeoverNewChat}
          isFirstRun={isFirstRun}
          takeover={{
            title: assistantOnboarding(onboarding!.id).title,
            initial: mountBoxRef.current,
            animate: { rect, borderRadius },
            transition: rectTransition,
            mark,
            contentVariant: phase === 'collapsing' ? 'hidden' : 'shown',
            onAnimationComplete: handleRectAnimationComplete,
          }}
        />
      ) : (
        <AssistantPanel isOpen={isOpen} chat={chat} onClose={onClose} corner={corner} isFirstRun={isFirstRun} />
      )}
    </>
  );
}
